import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';

const ProductForm = ({ product, onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    slno: '',
    description: '',
    hsncode: '',
    tax: '',
    price: ''
  });
  const [errors, setErrors] = useState({});

  // Populate form data when product prop changes
  useEffect(() => {
    if (product) {
      setFormData({
        slno: product.slno || '',
        description: product.description || '',
        hsncode: product.hsncode || '',
        tax: product.tax || '',
        price: product.price ?? product.rate ?? ''
      });
    } else {
      // Reset form for new product
      setFormData({
        slno: '',
        description: '',
        hsncode: '',
        tax: '',
        price: ''
      });
    }
  }, [product]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.slno || formData.slno.toString().trim() === '') {
      newErrors.slno = 'Code is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.hsncode || formData.hsncode <= 0) {
      newErrors.hsncode = 'HSN code is required and must be greater than 0';
    }
    
    if (formData.tax === '' || formData.tax < 0 || formData.tax > 100) {
      newErrors.tax = 'Tax rate is required and must be between 0 and 100';
    }

    if (formData.price === '' || formData.price < 0) {
      newErrors.price = 'Price is required and must be greater than or equal to 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Convert hsncode and tax to numbers, keep slno as string
      const submitData = {
        ...formData,
        slno: formData.slno.toString().trim(),
        hsncode: parseInt(formData.hsncode),
        tax: parseFloat(formData.tax),
        price: parseFloat(formData.price)
      };
      onSubmit(submitData);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group className="mb-3">
        <Form.Label>Code <span className="text-danger">*</span></Form.Label>
        <Form.Control
          type="text"
          name="slno"
          value={formData.slno}
          onChange={handleChange}
          isInvalid={!!errors.slno}
          placeholder="Enter product code"
        />
        <Form.Control.Feedback type="invalid">
          {errors.slno}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Description <span className="text-danger">*</span></Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          name="description"
          value={formData.description}
          onChange={handleChange}
          isInvalid={!!errors.description}
          placeholder="Enter product description"
        />
        <Form.Control.Feedback type="invalid">
          {errors.description}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>HSN Code <span className="text-danger">*</span></Form.Label>
        <Form.Control
          type="number"
          min="1"
          name="hsncode"
          value={formData.hsncode}
          onChange={handleChange}
          isInvalid={!!errors.hsncode}
          placeholder="Enter HSN code"
        />
        <Form.Control.Feedback type="invalid">
          {errors.hsncode}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Price <span className="text-danger">*</span></Form.Label>
        <Form.Control
          type="number"
          step="0.01"
          min="0"
          name="price"
          value={formData.price}
          onChange={handleChange}
          isInvalid={!!errors.price}
          placeholder="Enter product price"
        />
        <Form.Control.Feedback type="invalid">
          {errors.price}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Tax Rate (%) <span className="text-danger">*</span></Form.Label>
        <Form.Control
          type="number"
          step="0.01"
          min="0"
          max="100"
          name="tax"
          value={formData.tax}
          onChange={handleChange}
          isInvalid={!!errors.tax}
          placeholder="Enter tax rate percentage"
        />
        <Form.Control.Feedback type="invalid">
          {errors.tax}
        </Form.Control.Feedback>
      </Form.Group>

      <div className="d-flex gap-2 justify-content-end">
        <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={isLoading}>
          {isLoading && <Spinner animation="border" size="sm" className="me-2" />}
          {product ? 'Update Product' : 'Add Product'}
        </Button>
      </div>
    </Form>
  );
};

export default ProductForm;